// @spec SHL-STR-020

import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'quantifier-recent-commands';
const MAX_RECENT_COMMANDS = 5;

export interface UseRecentCommandsReturn {
  /** List of recent command IDs */
  recentCommands: string[];
  /** Add a command to recent history */
  addRecentCommand: (id: string) => void;
  /** Remove a specific command from history */
  removeRecentCommand: (id: string) => void;
  /** Clear all recent commands */
  clearRecentCommands: () => void;
}

/**
 * Hook for managing recent commands in localStorage.
 *
 * Stores up to 5 recent commands, deduplicates entries
 * (moving repeated commands to top).
 *
 * @example
 * const { recentCommands, addRecentCommand, clearRecentCommands } = useRecentCommands();
 *
 * // Add a command when user executes it
 * addRecentCommand('go-dashboard');
 *
 * // Get list of recent command IDs
 * recentCommands.forEach(id => console.log(id));
 */
export function useRecentCommands(): UseRecentCommandsReturn {
  const [recentCommands, setRecentCommands] = useState<string[]>([]);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: unknown = JSON.parse(stored);
        if (
          Array.isArray(parsed) &&
          parsed.every((item): item is string => typeof item === 'string')
        ) {
          setRecentCommands(parsed);
        }
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  // Persist to localStorage whenever commands change
  const persistCommands = useCallback((commands: string[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(commands));
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  const addRecentCommand = useCallback(
    (id: string) => {
      if (!id.trim()) return;

      setRecentCommands((prev) => {
        // Remove if already exists (will be re-added at top)
        const filtered = prev.filter((s) => s !== id);
        // Add to beginning and limit to max
        const updated = [id, ...filtered].slice(0, MAX_RECENT_COMMANDS);
        persistCommands(updated);
        return updated;
      });
    },
    [persistCommands]
  );

  const removeRecentCommand = useCallback(
    (id: string) => {
      setRecentCommands((prev) => {
        const updated = prev.filter((s) => s !== id);
        persistCommands(updated);
        return updated;
      });
    },
    [persistCommands]
  );

  const clearRecentCommands = useCallback(() => {
    setRecentCommands([]);
    persistCommands([]);
  }, [persistCommands]);

  return {
    recentCommands,
    addRecentCommand,
    removeRecentCommand,
    clearRecentCommands,
  };
}
